"use client";

import React from "react";
import { ArrowUpRight, Cable, CircleCheck as CheckCircle2, Cpu, ShieldAlert, Wind } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const specifications = [{
  label: "Controller",
  detail: "Confirm what type of controller and safety stop is included",
  icon: Cpu
}, {
  label: "Safety Arena",
  detail: "Confirm the inflatable safety arena included with the package",
  icon: ShieldAlert
}, {
  label: "Blower",
  detail: "Confirm whether a blower is included and its condition",
  icon: Wind
}, {
  label: "Cables & Accessories",
  detail: "Confirm what cables, remotes or accessories come with the machine",
  icon: Cable
}];
export default function BullPrices() {
  const shouldReduceMotion = useReducedMotion();
  const containerAnimation = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="newmechanicalbullprices" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerAnimation} className="relative bg-card text-card-foreground border border-border shadow-xs">
          {/* Top Machined Datum Accent */}
          <div className="flex items-center justify-between border-b border-border bg-muted px-4 py-2 text-muted-foreground font-mono text-xs uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 bg-primary" />
              <span>Equipment Pricing Protocol</span>
            </div>
          </div>

          <div className="p-6 md:p-10 lg:p-12 space-y-8">
            {/* Header */}
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest">
                <CheckCircle2 className="size-3.5" />
                <span>Package Evaluation Guide</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
                New Mechanical Bull Prices
              </h2>
            </div>

            {/* Authoritative Body Copy */}
            <div className="space-y-4 text-foreground/90 font-sans text-base md:text-lg leading-relaxed border-l-2 border-primary/40 pl-4 md:pl-6">
              <p>
                A new mechanical bull price should be evaluated against the complete equipment package. Before comparing two new machines, check whether each listing includes the same type of controller, inflatable safety arena, blower, cables and accessories. A lower advertised price may simply represent a different package. Therefore, look at what you are actually receiving before deciding which new machine offers better value. Use the New Bulls page to compare current machines, prices and product-specific information.
              </p>
            </div>

            {/* Specification Plate Callout */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase text-muted-foreground tracking-wider">
                  Mandatory Package Verification Checklist
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  4 Critical Components
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {specifications.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.label} data-index={i} className="border border-border bg-muted/40 p-4 transition-colors hover:bg-muted/70 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-end mb-2">
                          <Icon className="size-4 text-primary" />
                        </div>
                        <h3 className="font-serif text-lg font-semibold uppercase text-foreground tracking-wide">
                          {item.label}
                        </h3>
                      </div>
                      <p className="font-sans text-xs text-muted-foreground mt-2 leading-snug">
                        {item.detail}
                      </p>
                    </div>;
              })}
              </div>
            </div>

            {/* Action Trigger */}
            <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="font-mono text-xs text-muted-foreground">
                Complete machinery breakdown available on catalog index
              </div>

              <Button asChild size="lg" className="font-serif uppercase tracking-wider text-base bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 h-auto active:scale-[0.99] transition-transform">
                <Link to="/NewMechanicalBullsPage" className="inline-flex items-center justify-center gap-2">
                  <span>COMPARE NEW MECHANICAL BULLS</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Bottom Machined Datum Accent */}
          <div className="flex items-center justify-between border-t border-border bg-muted px-4 py-1.5 text-muted-foreground font-mono text-2xs uppercase">
            <span>U.S. Commercial Equipment Retail</span>
            <span>Compare Before You Buy</span>
          </div>
        </motion.div>
      </div>
    </section>;
}